/* Copyright (c) 2020-present CloudIO Inc */

import { IoFunctions, Query } from '@cloudio-saas/datasource-types';
import { emptyFunction, useStateXSelector } from '@cloudio-saas/statex';
import { FormControl, FormHelperText } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FunctionURIChooserProp } from 'componentregistry';
import { VirtualizedSelectInput } from 'custom/SelectInput';
import { pageAtom } from 'designer/atoms';
import { helpTextSelector } from 'designer/state';
import { memo, useEffect, useMemo, useState } from 'react';
import { itemWithDefaultValues } from 'registry/Components';
import useAppStore from 'store/useAppStore';
import WaveDots from 'utils/WaveDots';
import { getDefaultValues, isRequired } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles({
  root: {
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
});

export const dsFilter: () => Query<IoFunctions> = () => ({
  filter: [],
  limit: 5000,
  offset: 0,
  projection: {
    functionName: 1,
    functionUri: 1,
  },
  sort: { functionName: 1 },
});

export function FunctionURIChooser({
  appUid,
  helpText,
  label,
  onChange,
  required,
  testid,
  value,
  isViewOnlyUser = false,
}: {
  appUid: string;
  helpText?: string;
  label: string;
  onChange: (val: string) => void;
  required?: boolean;
  testid: string;
  value: string;
  isViewOnlyUser?: boolean;
}) {
  const { query, records, isBusy } = useAppStore<IoFunctions>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoFunctions',
    alias: 'IoFunctions-alias',
  });
  useEffect(() => {
    query({ props: dsFilter() });
  }, [appUid, query]);
  const classes = useStyles();

  const options = useMemo(
    () =>
      Object.values(records).map((row) => ({
        value: row.functionUri,
        label: row.functionName,
      })),
    [records],
  );

  return (
    <FormControl
      data-testid={testid}
      required={required}
      fullWidth
      variant="standard"
      component="fieldset"
      className={classes.root}
    >
      <VirtualizedSelectInput
        value={value}
        options={options}
        onChange={onChange}
        label={label}
        startIcon={isBusy ? <WaveDots /> : null}
        disabled={isBusy || isViewOnlyUser}
        placeholder={isBusy ? 'Loading ...' : 'Choose a function'}
        variant="standard"
        required={required}
        disableClearable={false}
      />
      <FormHelperText>{helpText}</FormHelperText>
    </FormControl>
  );
}

function FunctionURIChooserProperty({
  appUid,
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value = getDefaultValues<string>(mgr, property),
  onChange,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<FunctionURIChooserProp, string>) {
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const handleChange = (functionUri: string) => {
    if (functionUri !== value) {
      onChange(property, functionUri);
    }
  };

  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });

  const { getAppUid } = def;
  let fnAppUid = appUid;
  if (getAppUid) {
    const page = platform.getter(pageAtom, { params: { appUid, pageId } });
    if (page) {
      const item = itemWithDefaultValues(page[activeItemId]);
      fnAppUid =
        getAppUid(page, item, {
          pageId,
          appUid,
          itemId: activeItemId,
          mgr,
          platform,
        }) || appUid;
    }
  }

  const [, setState] = useState([]);

  useEffect(() => {
    def.refreshCallback(() => {
      setState([]);
    });
    return () => {
      def.refreshCallback(emptyFunction);
    };
  }, [activeItemId, appUid, def, pageId, platform]);

  return (
    <FunctionURIChooser
      isViewOnlyUser={isViewOnlyUser}
      appUid={fnAppUid}
      testid={property}
      value={value}
      onChange={handleChange}
      label={def.label}
      required={required}
      helpText={helpText}
    />
  );
}

export default memo(FunctionURIChooserProperty);
